import * as React from 'react';
import { Link } from 'react-router-dom';
import { useAuth, ApiError } from '@/lib/auth';
import { isCognitoEnabled } from '@/lib/cognito';
import { Button } from '@/components/ui';
import { useI18n } from '@/lib/i18n';
import logoUrl from '@/assets/suzuki-logo.png';

export function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const cognitoMode = isCognitoEnabled();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await forgotPassword(email);
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        // Always show success to avoid email enumeration
        setSubmitted(true);
      } else {
        setError(t('common.unexpectedError'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface-page">
      <div className="h-1.5 bg-brand-600" aria-hidden="true" />
      <div className="flex flex-1 items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8 rounded-xl border border-line bg-surface p-8 shadow-raised">
          <div className="text-center">
            <img src={logoUrl} alt="Suzuki" className="mx-auto mb-4 h-12" />
            <h1 className="text-2xl font-bold text-ink">{t('auth.resetTitle')}</h1>
            <p className="mt-2 text-sm text-ink-muted">{t('auth.resetSubtitle')}</p>
          </div>

          {submitted ? (
            <div className="space-y-4 text-center">
              <div className="rounded-xl border border-success-500/20 bg-success-50 p-4 text-sm text-success-700" role="status">
                {t(cognitoMode ? 'auth.checkCode' : 'auth.checkEmail')}
              </div>
              {cognitoMode && (
                <Link
                  to={`/reset-password?email=${encodeURIComponent(email)}`}
                  className="block rounded-lg bg-brand-600 px-4 py-2 text-center text-sm font-semibold text-white shadow-card transition-all duration-150 hover:bg-brand-700 hover:shadow-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                >
                  {t('auth.continueToReset')}
                </Link>
              )}
              <Link
                to="/login"
                className="inline-block text-sm font-medium text-brand-700 transition-colors hover:text-brand-800 hover:underline"
              >
                &larr; {t('common.backToLogin')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-6" noValidate>
              {error && (
                <div className="rounded-xl border border-accent-100 bg-accent-50 p-3 text-sm text-accent-700" role="alert">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-ink-muted">
                  {t('auth.emailAddress')}
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-line-strong px-3 py-2 shadow-card transition-all duration-150 hover:border-brand-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
                  placeholder="you@suzuki-digital-test.com"
                />
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? t('auth.sending') : t('auth.sendResetLink')}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm font-medium text-brand-700 transition-colors hover:text-brand-800 hover:underline"
                >
                  &larr; {t('common.backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
